import { useEffect, useState } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { AivaSparkleIcon } from "../../../../../shared/components/icons";
import { MAX_USER_DESCRIPTION_CHARS } from "../../Infographic/services/infographicApi";
import { useGetInsights } from "../hooks/useGetInsights";
import { useInsightsFollowUp } from "../hooks/useInsightsFollowUp";
import type {
  GetInsightsState,
  InsightsFollowUpAction,
  InsightsResponse,
  InsightsSelectionType,
} from "../../../types";
import styles from "./GetInsightsPanel.module.css";

const SELECTION_LABEL: Record<InsightsSelectionType, string> = {
  chart: "chart",
  table: "table",
  image: "image",
  slide: "slide",
};

const INFOGRAPHIC_GUIDANCE_PREFIX = "Emphasize these insights:";

/** Turns the insight bullets into Convert to Infographic's free-text steering
 *  description — the same field its own textarea fills. */
function infographicGuidance(insights: readonly string[]): string {
  const guidance = `${INFOGRAPHIC_GUIDANCE_PREFIX} ${insights.join("; ")}`;
  return guidance.slice(0, MAX_USER_DESCRIPTION_CHARS);
}

interface GetInsightsPanelProps {
  readonly onBack?: () => void;
  /** Routes to the Convert to Infographic panel with the insight bullets as
   *  its starting description — Get Insights doesn't run the conversion
   *  itself, so the user sees that panel's own progress UI. */
  readonly onMakeInfographic?: (initialDescription: string) => void;
}

export function GetInsightsPanel({ onBack, onMakeInfographic }: Readonly<GetInsightsPanelProps>) {
  const { state, analyze, retry, cancel } = useGetInsights();

  return (
    <div className={styles.panel}>
      <ModuleHeader title="Get Insights" subtitle="Deckorate" onBack={onBack} />
      {renderBody(state, analyze, retry, cancel, onMakeInfographic)}
    </div>
  );
}

function renderBody(
  state: Readonly<GetInsightsState>,
  analyze: () => void,
  retry: () => void,
  cancel: () => void,
  onMakeInfographic?: (initialDescription: string) => void,
) {
  if (state.status === "empty") {
    return <EmptyState />;
  }
  if (state.status === "idle") {
    return <IdleState selectionType={state.selection.type} onAnalyze={analyze} />;
  }
  if (state.status === "loading") {
    return <LoadingState selectionType={state.selection.type} onCancel={cancel} />;
  }
  if (state.status === "ready") {
    return <InsightsResult insights={state.insights} onMakeInfographic={onMakeInfographic} />;
  }
  return <ErrorState message={state.message} onRetry={retry} />;
}

// Nothing is selected yet — there's no target for the button to analyse.
function EmptyState() {
  return (
    <div className={styles.emptyState}>
      <p className={styles.emptyText}>Select an image, chart, table, or slide to get insights.</p>
    </div>
  );
}

// A supported selection is in place but analysis hasn't been requested yet —
// the user presses "Get Insights" to actually send it to the backend.
function IdleState({
  selectionType,
  onAnalyze,
}: Readonly<{ selectionType: InsightsSelectionType; onAnalyze: () => void }>) {
  return (
    <div className={styles.idleState}>
      <p className={styles.idleText}>Ready to analyse this {SELECTION_LABEL[selectionType]}.</p>
      <button type="button" className={styles.analyzeBtn} onClick={onAnalyze}>
        Get Insights
      </button>
    </div>
  );
}

function LoadingState({
  selectionType,
  onCancel,
}: Readonly<{ selectionType: InsightsSelectionType; onCancel: () => void }>) {
  return (
    <div className={styles.loadingState}>
      <span className={styles.spinner} aria-hidden="true" />
      <p className={styles.loadingText}>Analysing {SELECTION_LABEL[selectionType]}…</p>
      <button type="button" className={styles.cancelBtn} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

function ErrorState({ message, onRetry }: Readonly<{ message: string; onRetry: () => void }>) {
  return (
    <div className={`${styles.statusBox} ${styles.statusError}`}>
      <p className={styles.statusLabel}>{message}</p>
      <button type="button" className={styles.retryBtn} onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

/** "just now" / "N mins ago", ticking every 30s — a result stays on screen
 *  long enough that a frozen "just now" would go stale and mislead. */
function useRelativeTime(anchor: number): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const diffMin = Math.floor((now - anchor) / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return diffMin === 1 ? "1 min ago" : `${diffMin} mins ago`;
  const diffHr = Math.floor(diffMin / 60);
  return diffHr === 1 ? "1 hr ago" : `${diffHr} hrs ago`;
}

function followUpStatusMessage(
  action: InsightsFollowUpAction,
  directResult: ReturnType<typeof useInsightsFollowUp>["directResult"],
): { text: string; isError: boolean } | null {
  if (action === "make_infographic" || directResult.status === "idle" || directResult.action !== action) {
    return null;
  }
  if (directResult.status === "busy") return { text: "Working on it…", isError: false };
  return { text: directResult.message, isError: directResult.status === "error" };
}

function InsightsResult({
  insights,
  onMakeInfographic,
}: Readonly<{ insights: InsightsResponse; onMakeInfographic?: (initialDescription: string) => void }>) {
  const [readyAt] = useState(() => Date.now());
  const relativeTime = useRelativeTime(readyAt);
  const { directResult, run } = useInsightsFollowUp();

  if (insights.insights.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyText}>No insights could be generated for this selection.</p>
      </div>
    );
  }

  const anyBusy = directResult.status === "busy";
  const activeAction = anyBusy ? directResult.action : null;

  const handlePillClick = (action: InsightsFollowUpAction) => {
    if (action === "make_infographic") {
      onMakeInfographic?.(infographicGuidance(insights.insights));
      return;
    }
    void run(action, insights.insights);
  };

  return (
    <div className={styles.resultPage}>
      <div className={styles.card}>
        <div className={styles.avatar}>
          <AivaSparkleIcon />
        </div>
        <div className={styles.cardBody}>
          <div className={styles.meta}>
            <strong className={styles.metaBrand}>AiVA</strong> &middot; {relativeTime}
          </div>
          <div className={styles.bubble}>
            <h3 className={styles.bubbleTitle}>Insights</h3>
            <ul className={styles.sectionList}>
              {insights.insights.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          {insights.followUpOptions.length > 0 && (
            <>
              <hr className={styles.pillDivider} />
              <div className={styles.pillRow}>
                {insights.followUpOptions.map((option) => (
                  <button
                    key={option.action}
                    type="button"
                    className={styles.pill}
                    disabled={anyBusy}
                    onClick={() => handlePillClick(option.action)}
                  >
                    {activeAction === option.action ? "Working…" : option.label}
                  </button>
                ))}
              </div>
              {insights.followUpOptions.map((option) => {
                const status = followUpStatusMessage(option.action, directResult);
                if (!status) return null;
                return (
                  <p key={option.action} className={status.isError ? styles.followUpError : styles.followUpSuccess}>
                    {status.text}
                  </p>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
